import { AnyAction } from 'redux';
import { DocumentType } from '@synergy/models';

import {
    ACTIVITIES_COMMENT_ADDED,
    ACTIVITIES_COMMENT_DELETED,
    ACTIVITIES_COMMENT_UPDATED,
    ACTIVITIES_RECEIVED,
    ACTIVITIES_RESET,
    RESET_DOCUMENT_TYPE,
    SET_DOCUMENT_TYPE,
} from '../actions/activity-actions';
import { Activity } from '../models/activity';
import { ActivityComment } from '../models/activity-comment';

export interface ActivityReducerState {
    items: Activity[];
    documentType?: DocumentType;
    /**
     * Counts how often the activity log was loaded. The masks refetch it after
     * a save, so it doubles as the signal that the document gained history —
     * the stream panel reloads on it, exactly when the old panel refreshed.
     */
    loadCount: number;
}

const initialActivityReducerState: ActivityReducerState = {
    items: [],
    loadCount: 0,
};

const addComment = (activities: Activity[], activityId: number, comment: ActivityComment): Activity[] => {
    const existingActivity = activities.find(activity => activity.id === activityId);
    if (!existingActivity) {
        return activities;
    }
    existingActivity.comments = [...existingActivity.comments, comment];
    return [...activities];
};

const updateComment = (activities: Activity[], activityId: number, updatedComment: ActivityComment): Activity[] => {
    const existingActivity = activities.find(activity => activity.id === activityId);
    if (!existingActivity) {
        return activities;
    }
    const commentsWithoutUpdated = existingActivity.comments.filter(comment => comment.id !== updatedComment.id);
    existingActivity.comments = [...commentsWithoutUpdated, updatedComment];
    return [...activities];
};

const deleteComment = (activities: Activity[], activityId: number, commentIdToDelete: number): Activity[] => {
    const existingActivity = activities.find(activity => activity.id === activityId);
    if (!existingActivity) {
        return activities;
    }
    existingActivity.comments = existingActivity.comments.filter(comment => comment.id !== commentIdToDelete);
    return [...activities];
};

export const activitiesReducer = (
    state: ActivityReducerState = initialActivityReducerState,
    action: AnyAction
): ActivityReducerState => {
    const { type } = action;
    switch (type) {
        case ACTIVITIES_RECEIVED: {
            return {
                ...state,
                items: action['activities'],
                loadCount: state.loadCount + 1,
            };
        }

        case ACTIVITIES_RESET: {
            return {
                ...state,
                items: initialActivityReducerState.items,
            };
        }

        case ACTIVITIES_COMMENT_ADDED: {
            return {
                ...state,
                items: addComment(state.items, action['activityId'], action['comment']),
            };
        }

        case ACTIVITIES_COMMENT_UPDATED: {
            return {
                ...state,
                items: updateComment(state.items, action['activityId'], action['comment']),
            };
        }

        case ACTIVITIES_COMMENT_DELETED: {
            return {
                ...state,
                items: deleteComment(state.items, action['activityId'], action['commentId']),
            };
        }

        case SET_DOCUMENT_TYPE: {
            return {
                ...state,
                documentType: action['documentType'],
            };
        }

        case RESET_DOCUMENT_TYPE: {
            return {
                ...state,
                documentType: undefined,
            };
        }

        default: {
            return state;
        }
    }
};
